import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import api from "../../services/api";
import "./styles.css";

const publicClient = axios.create({
  baseURL:
    api.defaults.baseURL ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://api.usekorax.com",
  timeout: 20000
});

const hasChoices = type =>
  ["select", "radio", "multiselect"].includes(type);

const errorMessage = error => {
  const status = error?.response?.status;

  if (status === 404) {
    return "Este formulário não foi encontrado ou não está disponível.";
  }

  if (status === 410) {
    return "Este formulário expirou ou já foi respondido.";
  }

  if (status === 429) {
    return "Muitas tentativas. Aguarde alguns minutos.";
  }

  if (status === 400) {
    return error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Confira as informações preenchidas.";
  }

  return "Não foi possível concluir a solicitação. Tente novamente.";
};

function PublicField({ field, value, onChange }) {
  const options = Array.isArray(field.options)
    ? field.options
    : [];

  const id = `public-field-${field.key}`;
  const required = Boolean(field.required);

  const descriptionId = field.helpText
    ? `${id}-help`
    : undefined;

  const common = {
    id,
    required,
    "aria-describedby": descriptionId
  };

  const stringValue =
    typeof value === "string" ? value : "";

  let control;

  if (field.type === "textarea") {
    control = (
      <textarea
        {...common}
        rows={4}
        maxLength={4000}
        value={stringValue}
        placeholder={field.placeholder || "Sua resposta"}
        onChange={event => onChange(event.target.value)}
      />
    );
  } else if (field.type === "select") {
    control = (
      <select
        {...common}
        value={stringValue}
        onChange={event => onChange(event.target.value)}
      >
        <option value="">Selecione uma opção</option>
        {options.map((option, index) => (
          <option key={`${index}-${option}`} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "radio") {
    control = (
      <div className="kfp-choices">
        {options.map((option, index) => (
          <label className="kfp-choice" key={index}>
            <input
              type="radio"
              name={id}
              value={option}
              required={required}
              checked={stringValue === option}
              onChange={() => onChange(option)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  } else if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];

    control = (
      <div className="kfp-choices">
        {options.map((option, index) => (
          <label className="kfp-choice" key={index}>
            <input
              type="checkbox"
              value={option}
              checked={selected.includes(option)}
              onChange={event => {
                const next = event.target.checked
                  ? [...selected, option]
                  : selected.filter(item => item !== option);

                onChange(next);
              }}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  } else if (field.type === "checkbox") {
    control = (
      <label className="kfp-confirmation">
        <input
          id={id}
          type="checkbox"
          required={required}
          checked={value === true}
          onChange={event => onChange(event.target.checked)}
        />
        <span>Confirmo esta informação</span>
      </label>
    );
  } else {
    const type =
      field.type === "email"
        ? "email"
        : field.type === "date"
          ? "date"
          : field.type === "phone"
            ? "tel"
            : "text";

    control = (
      <input
        {...common}
        type={type}
        maxLength={500}
        value={stringValue}
        placeholder={field.placeholder || "Digite sua resposta"}
        inputMode={
          ["cpf", "cnpj"].includes(field.type)
            ? "numeric"
            : field.type === "phone"
              ? "tel"
              : undefined
        }
        autoComplete={
          field.type === "email"
            ? "email"
            : field.type === "phone"
              ? "tel"
              : "off"
        }
        onChange={event => onChange(event.target.value)}
      />
    );
  }

  return (
    <div className="kfp-field">
      <label
        htmlFor={
          ["radio", "multiselect"].includes(field.type)
            ? undefined
            : id
        }
        className="kfp-field-label"
      >
        {field.label}
        {required && <span aria-hidden="true"> *</span>}
      </label>

      {field.helpText && (
        <p id={descriptionId} className="kfp-field-help">
          {field.helpText}
        </p>
      )}

      {control}
    </div>
  );
}

export default function KoraxFormPublic() {
  const { publicId, token } = useParams();

  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");

  const endpoint =
    `/korax-forms/public/${encodeURIComponent(publicId || "s")}` +
    `/${encodeURIComponent(token)}`;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Formulário de atendimento";

    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);

    let active = true;

    const load = async () => {
      setLoading(true);

      try {
        const response = await publicClient.get(endpoint);

        if (!active) return;

        if (!Array.isArray(response.data?.fields)) {
          throw new Error("INVALID_FORM_STRUCTURE");
        }

        setForm(response.data);
        setError("");
      } catch (err) {
        if (active) {
          setError(errorMessage(err));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
      document.title = previousTitle;
      meta.remove();
    };
  }, [endpoint]);

  const updateAnswer = (key, value) => {
    setFieldError("");

    setAnswers(previous => ({
      ...previous,
      [key]: value
    }));
  };

  const submit = async event => {
    event.preventDefault();

    if (!form || sending || completed) return;

    for (const field of form.fields) {
      if (!field.required) continue;

      const value = answers[field.key];

      const empty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && !value.trim()) ||
        (Array.isArray(value) && value.length === 0) ||
        (field.type === "checkbox" && value !== true);

      if (empty) {
        setFieldError(
          `Preencha o campo obrigatório: ${field.label}.`
        );
        return;
      }

      if (
        hasChoices(field.type) &&
        Array.isArray(value) &&
        value.length > 25
      ) {
        setFieldError("Selecione menos opções.");
        return;
      }
    }

    setSending(true);
    setError("");
    setFieldError("");

    try {
      await publicClient.post(endpoint, { answers });

      setCompleted(true);
      setAnswers({});
      setForm(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const stateScreen = (symbol, title, description) => (
    <div className="kfp-state" role="status">
      <div className="kfp-state-symbol">{symbol}</div>
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
  );

  return (
    <main className="kfp-root">
      <div className="kfp-container">
        <div className="kfp-topline">
          <span className="kfp-topline-dot" />
          FORMULÁRIO DE ATENDIMENTO
        </div>

        <div className="kfp-card">
          {loading ? (
            stateScreen(
              "◌",
              "Carregando formulário",
              "Aguarde um instante..."
            )
          ) : completed ? (
            stateScreen(
              "✓",
              "Informações recebidas!",
              "Seu formulário foi enviado com sucesso. " +
              "Você já pode retornar à conversa no WhatsApp."
            )
          ) : !form ? (
            stateScreen(
              "!",
              "Formulário indisponível",
              error || "Não foi possível abrir este formulário."
            )
          ) : (
            <>
              <header className="kfp-header">
                <div className="kfp-header-icon">▤</div>
                <h1>{form.title}</h1>
                <p>
                  {form.description ||
                    "Preencha as informações abaixo para " +
                    "continuarmos seu atendimento."}
                </p>
              </header>

              <div className="kfp-divider" />

              <form onSubmit={submit}>
                <div className="kfp-form-intro">
                  <h2>Suas informações</h2>
                  <p>
                    Preencha os campos com atenção.
                    Os campos com * são obrigatórios.
                  </p>
                </div>

                {(form.fields || []).map((field, index) => (
                  <PublicField
                    key={field.key}
                    field={field}
                    value={answers[field.key]}
                    onChange={value =>
                      updateAnswer(field.key, value)
                    }
                  />
                ))}

                {fieldError && (
                  <p className="kfp-alert" role="alert">
                    {fieldError}
                  </p>
                )}

                {error && (
                  <p className="kfp-alert" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="kfp-submit"
                  disabled={sending}
                >
                  {sending
                    ? "Enviando informações..."
                    : "Enviar informações →"}
                </button>

                <p className="kfp-privacy">
                  Suas respostas serão encaminhadas à empresa
                  responsável pelo atendimento.
                </p>
              </form>
            </>
          )}
        </div>

        <p className="kfp-footer">
          Formulário seguro de atendimento
        </p>
      </div>
    </main>
  );
}
